import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSiteStore } from '../stores/siteStore';
import { workerAnalyticsAPI } from '../utils/api';
import { formatDate } from '../utils/formatDateTime';
import ContractorComparisonBars from '../components/analytics/ContractorComparisonBars';
import {
  BuildingOfficeIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const RISK_BADGE = {
  HIGH: 'bg-red-100 text-red-700',
  MEDIUM: 'bg-amber-100 text-amber-700',
  LOW: 'bg-emerald-100 text-emerald-700',
};

export default function ContractorAnalyticsPage() {
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id;
  const [period, setPeriod] = useState('30d');
  const [sortBy, setSortBy] = useState('safetyRating');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['contractor-performance', siteId, period, customStartDate, customEndDate],
    queryFn: () => {
      const params = { siteId, period };
      if (period === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      return workerAnalyticsAPI.getContractorPerformance(params).then(r => r.data);
    },
    enabled: Boolean(siteId && (period !== 'custom' || (customStartDate && customEndDate))),
  });

  const { data: comparisonData } = useQuery({
    queryKey: ['contractor-comparison', selectedForCompare.join(','), period, customStartDate, customEndDate],
    queryFn: () => {
      const params = {
        contractorIds: selectedForCompare.join(','),
        period,
      };
      if (period === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      return workerAnalyticsAPI.getContractorComparison(params).then(r => r.data);
    },
    enabled: Boolean(selectedForCompare.length >= 2 && (period !== 'custom' || (customStartDate && customEndDate))),
  });

  const summary = data?.summary || {};
  const contractors = data?.contractors || [];

  // Sort contractors
  const sorted = [...contractors].sort((a, b) => {
    const aVal = a[sortBy] ?? 0;
    const bVal = b[sortBy] ?? 0;
    return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
  });

  function toggleSort(field) {
    if (sortBy === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  }

  function toggleCompare(id) {
    setSelectedForCompare(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 5 ? [...prev, id] : prev
    );
  }

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <span className="text-slate-300 ml-1">&udarr;</span>;
    return <span className="text-slate-600 ml-1">{sortDir === 'desc' ? '\u2193' : '\u2191'}</span>;
  };

  // CSV export function
  const escapeCSVField = (field) => {
    const value = String(field ?? '');
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const handleExport = () => {
    try {
      const headers = ['Contractor', 'Code', 'Safety Rating', 'Workers', 'Recent Violations', 'Violations/Worker', 'Risk Score', 'Risk Level', 'Avg Worker Score', 'Contract End'];
      const data = sorted.map(c => [
        c.name,
        c.code || '-',
        c.safetyRating,
        c.workerCount,
        c.recentViolations,
        c.normalizedRate,
        c.riskScore,
        c.riskLevel,
        c.avgWorkerSafetyScore,
        c.contractEnd ? formatDate(c.contractEnd) : '-',
      ]);

      const csv = [
        headers.map(escapeCSVField).join(','),
        ...data.map(row => row.map(escapeCSVField).join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contractor-analytics-${period}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Contractor Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Safety performance and risk assessment by contractor</p>
        </div>
        <button
          onClick={handleExport}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium w-fit"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Period & Date Selection */}
      <div className="flex">
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {['7d', '30d', '90d'].map(p => (
              <button
                key={p}
                onClick={() => {
                  setPeriod(p);
                  setCustomStartDate('');
                  setCustomEndDate('');
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  period === p ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
            <button
              onClick={() => setPeriod('custom')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                period === 'custom' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Custom
            </button>
          </div>
          {period === 'custom' && (
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-2 py-1 text-xs bg-white border border-slate-300 rounded"
              />
              <span className="text-xs text-slate-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-2 py-1 text-xs bg-white border border-slate-300 rounded"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <BuildingOfficeIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-800">{summary.totalContractors || 0}</p>
              <p className="text-xs text-slate-500">Total Contractors</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-800">{summary.avgSafetyRating || 100}</p>
              <p className="text-xs text-slate-500">Avg Safety Rating</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-800">{summary.atRiskCount || 0}</p>
              <p className="text-xs text-slate-500">At-Risk Contractors</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <CalendarDaysIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-800">{summary.expiringContracts || 0}</p>
              <p className="text-xs text-slate-500">Expiring Contracts</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Safety Rating Bar Chart */}
        <div className="glass p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Contractor Safety Rankings</h3>
          <div className="space-y-2">
            {sorted.slice(0, 6).map((c) => {
              const riskColor = c.riskLevel === 'HIGH' ? 'bg-red-500' : c.riskLevel === 'MEDIUM' ? 'bg-amber-500' : 'bg-emerald-500';
              const pct = (c.safetyRating / 100) * 100;
              return (
                <div key={c.id} className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-600 w-24 truncate">{c.name}</span>
                  <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${riskColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-bold text-slate-700 w-8 text-right">{c.safetyRating}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Risk Distribution Donut */}
        <div className="glass p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Risk Level Distribution</h3>
          <div className="flex items-center justify-center">
            <div className="relative w-32 h-32">
              <svg width="100%" height="100%" viewBox="0 0 100 100" className="transform -rotate-90">
                {(() => {
                  const highCount = contractors.filter(c => c.riskLevel === 'HIGH').length;
                  const mediumCount = contractors.filter(c => c.riskLevel === 'MEDIUM').length;
                  const lowCount = contractors.filter(c => c.riskLevel === 'LOW').length;
                  const total = contractors.length || 1;

                  const highPct = (highCount / total) * 100;
                  const mediumPct = (mediumCount / total) * 100;
                  const lowPct = (lowCount / total) * 100;

                  const highDasharray = `${highPct} ${100 - highPct}`;
                  const mediumOffset = highPct;
                  const mediumDasharray = `${mediumPct} ${100 - mediumPct}`;
                  const lowOffset = highPct + mediumPct;
                  const lowDasharray = `${lowPct} ${100 - lowPct}`;

                  return (
                    <>
                      <circle cx="50" cy="50" r="35" fill="none" stroke="#ef4444" strokeWidth="8" strokeDasharray={highDasharray} strokeLinecap="round" />
                      <circle cx="50" cy="50" r="35" fill="none" stroke="#f59e0b" strokeWidth="8" strokeDasharray={mediumDasharray} strokeDashoffset={-mediumOffset} strokeLinecap="round" />
                      <circle cx="50" cy="50" r="35" fill="none" stroke="#10b981" strokeWidth="8" strokeDasharray={lowDasharray} strokeDashoffset={-lowOffset} strokeLinecap="round" />
                    </>
                  );
                })()}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-lg font-bold text-slate-800">{contractors.length}</p>
                  <p className="text-[10px] text-slate-500">contractors</p>
                </div>
              </div>
            </div>
            <div className="ml-6 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-xs text-slate-600">High: {contractors.filter(c => c.riskLevel === 'HIGH').length}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-xs text-slate-600">Medium: {contractors.filter(c => c.riskLevel === 'MEDIUM').length}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-xs text-slate-600">Low: {contractors.filter(c => c.riskLevel === 'LOW').length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Visual Contractor Comparison - shown when 2+ selected */}
      {selectedForCompare.length >= 2 && comparisonData?.contractors && (
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-semibold text-slate-700">Contractor Comparison</h3>
            <span className="text-xs text-slate-500 font-medium">{comparisonData.contractors.length} contractors selected</span>
          </div>
          <ContractorComparisonBars contractors={comparisonData.contractors} />
        </div>
      )}

      {/* Performance Table */}
      <div className="glass p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700">Contractor Performance</h3>
          {selectedForCompare.length >= 2 && (
            <span className="text-xs text-blue-600 font-medium">{selectedForCompare.length} selected for comparison</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">
                  <input
                    type="checkbox"
                    checked={selectedForCompare.length > 0 && selectedForCompare.length === sorted.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedForCompare(sorted.slice(0, 5).map(c => c.id));
                      } else {
                        setSelectedForCompare([]);
                      }
                    }}
                    className="rounded"
                  />
                </th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Contractor</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 cursor-pointer" onClick={() => toggleSort('safetyRating')}>
                  Safety Rating<SortIcon field="safetyRating" />
                </th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 cursor-pointer" onClick={() => toggleSort('workerCount')}>
                  Workers<SortIcon field="workerCount" />
                </th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 cursor-pointer" onClick={() => toggleSort('normalizedRate')}>
                  Violations/Worker<SortIcon field="normalizedRate" />
                </th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 cursor-pointer" onClick={() => toggleSort('riskScore')}>
                  Risk Score<SortIcon field="riskScore" />
                </th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">Risk Level</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Contract</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(c => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                  <td className="py-2 px-3">
                    <input
                      type="checkbox"
                      checked={selectedForCompare.includes(c.id)}
                      onChange={() => toggleCompare(c.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <p className="font-medium text-slate-700">{c.name}</p>
                    <p className="text-[10px] text-slate-400">{c.code}</p>
                  </td>
                  <td className="py-2 px-3 text-right font-bold text-slate-700">{c.safetyRating}</td>
                  <td className="py-2 px-3 text-right text-slate-600">{c.workerCount}</td>
                  <td className="py-2 px-3 text-right font-medium text-slate-700">{c.normalizedRate}</td>
                  <td className="py-2 px-3 text-right font-medium text-slate-700">{c.riskScore}</td>
                  <td className="py-2 px-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${RISK_BADGE[c.riskLevel] || RISK_BADGE.LOW}`}>
                      {c.riskLevel}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    {c.isExpiring ? (
                      <span className="text-xs text-amber-600 font-semibold">Expiring Soon</span>
                    ) : c.contractEnd ? (
                      <span className="text-xs text-slate-500">{formatDate(c.contractEnd)}</span>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-slate-400">No contractor data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
